import { APP_ID, enableProdMode, importProvidersFrom, isDevMode, signal } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { CoreEffects } from '@next/core-lib';
import { provideAgrAdvisorRelationshipsSelector } from '@next/core-lib/advisor-relationships-selector';
import { provideAgrCore } from '@next/core-lib/app';
import { AgrBrowserModule, I18nBrowserEffects } from '@next/core-lib/browser';
import {
  AgrPageDetailModule,
  AgrPageSidesheetModule,
  AgrPageToolbarModule,
  AgrPrimaryActionModule,
} from '@next/core-lib/components';
import { provideAgrElasticApi } from '@next/core-lib/elastic';
import { parseAgrEnvironment } from '@next/core-lib/environment';
import { ENVIRONMENT_MAPPER } from '@next/core-lib/environment-browser';
import { I18nEffects, TranslatePipe } from '@next/core-lib/i18n';
import { NavigationEffects } from '@next/core-lib/navigation';
import { provideAgrRouterStore } from '@next/core-lib/router-store';
import { provideAgrSettings } from '@next/core-lib/settings';
import { SiteEffects } from '@next/core-lib/site';
import { AgrWrapperSiteSidenavModule } from '@next/core-lib/wrapper-site-sidenav';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideHighcharts } from 'highcharts-angular';

import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';

const loadedConfig = signal(environment);

const setupRuntimeConfig = (async () => {
  try {
    const response = await globalThis.fetch('/assets/config.json');
    if (response.ok) {
      const config = await response.json();
      loadedConfig.set({ ...environment, ...parseAgrEnvironment(config) });
    } else {
      console.warn('No runtime configuration found, using default environment settings.');
    }
  } catch (e) {
    console.warn('Failed to load runtime environment variables', e);
  }
})();

if (environment.PRODUCTION) {
  enableProdMode();
}

setupRuntimeConfig
  .then(() =>
    bootstrapApplication(AppComponent, {
      providers: [
        importProvidersFrom(
          AgrBrowserModule,
          AgrWrapperSiteSidenavModule,
          AgrPageToolbarModule,
          AgrPrimaryActionModule,
          AgrPageSidesheetModule,
          AgrPageDetailModule,
        ),
        provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
        provideStore({}, {}),
        provideAgrRouterStore(),
        provideAgrCore(),
        provideAgrElasticApi(),
        provideAgrAdvisorRelationshipsSelector(),
        provideAgrSettings(),
        provideEffects([
          CoreEffects,
          I18nEffects,
          I18nBrowserEffects,
          SiteEffects,
          NavigationEffects,
        ]),
        provideStoreDevtools({
          maxAge: 25,
          logOnly: !isDevMode(),
          autoPause: true,
          trace: false,
          traceLimit: 75,
          connectInZone: false,
        }),
        provideHighcharts({
          modules: () => [
            import('highcharts/esm/highcharts-more.js'),
            import('highcharts/esm/modules/accessibility.js'),
          ],
        }),
        TranslatePipe,
        {
          provide: ENVIRONMENT_MAPPER,
          useFactory: () => loadedConfig(),
        },
        {
          provide: APP_ID,
          useFactory: () => loadedConfig().APPLICATION_NAME,
        },
      ],
    }),
  )
  .catch((err) => console.error(err));
